// src/pages/Admin/ViewCompany.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { authenticatedFetch, clearStoredCredentials } from '../../utils/api';

const API_BASE = process.env.REACT_APP_API_URL || 'https://fslapi.devformula.com/api';

function ViewCompany() {
  const navigate = useNavigate();
  const { companyId } = useParams();
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [dropdownOptions, setDropdownOptions] = useState({
    titles: [],
    businessSectors: [],
    businessCategories: []
  });
  const [previewModal, setPreviewModal] = useState({ show: false, url: '', type: '', title: '' });

  useEffect(() => {
    const adminSession = localStorage.getItem('fsl_admin_session');
    if (!adminSession) {
      toast.error('Please log in as admin');
      navigate('/admin-login', { replace: true });
      return;
    }
    fetchCompanyDetails();
  }, [companyId, navigate]);

  const fetchCompanyDetails = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch(
        `${API_BASE}/hmo/get-company-details`, 
        'POST', 
        { companyId: companyId }
      );
      
      if (response?.status === 'success') {
        setCompany(response.data);
        
        if (response.dropdowns) {
          setDropdownOptions({
            titles: response.dropdowns.titles || [],
            businessSectors: response.dropdowns.businessSectors || [],
            businessCategories: response.dropdowns.businessCategories || []
          });
        }
      } else {
        throw new Error(response?.message || 'Failed to load company details');
      }
    } catch (error) {
      console.error('💥 Fetch company error:', error);
      toast.error('Could not load company details');
    } finally {
      setLoading(false);
    }
  };

  const getDropdownName = (dropdown, id) => {
    if (!id) return 'N/A';
    const item = dropdown.find(d => d.id === id || d.name === id);
    return item ? item.name : id;
  };

  const handlePreviewDocument = (url, type, title) => {
    if (!url) {
      toast.error('Document not available');
      return;
    }
    const fullUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;
    setPreviewModal({ show: true, url: fullUrl, type, title });
  };

  const handleClosePreview = () => {
    setPreviewModal({ show: false, url: '', type: '', title: '' });
  };

  const handleLogout = (e) => {
    e?.preventDefault();
    clearStoredCredentials();
    localStorage.removeItem('fsl_admin_session');
    toast.success('Logged out successfully');
    navigate('/admin-login', { replace: true });
  };

  const navItems = [
    { 
      section: 'MAIN', 
      items: [
        { name: 'Get Started', icon: 'fa-home', path: '/get-started' }
      ] 
    },
    { 
      section: 'DASHBOARD', 
      items: [
        { name: 'Analytics', icon: 'fa-chart-bar', path: '/admin' },
      ] 
    },
    {
      section: 'ADMIN',
      items: [
        { name: 'Client', icon: 'fa-building', path: '/admin/companies' },
        { name: 'Documents', icon: 'fa-file-contract', path: '/admin/company-documents' },
        { name: 'Settings', icon: 'fa-cog', path: '/admin/crud/title' }
      ]
    }
  ];

  if (loading) {
    return (
      <div className="admin-page">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
          <p className="mt-3 text-muted">Loading company details...</p>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="admin-page">
        <div className="text-center py-5">
          <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
          <h5>Company not found</h5>
          <button className="btn btn-primary mt-3" onClick={() => navigate('/admin/companies')}>
            Back to Clients
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <ToastContainer position="top-right" autoClose={3000} theme="light" />
      
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <Link to="/" className="brand-logo">
            <img src="../images/fsl_logo.png" alt="Logo" style={{ height: '50px' }} />
          </Link>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((section, idx) => (
            <div key={idx} className="nav-section">
              <h6 className="nav-section-title">{section.section}</h6>
              <ul className="nav-list">
                {section.items.map((item, itemIdx) => (
                  <li key={itemIdx}>
                    <Link to={item.path} className="nav-link">
                      <i className={`fas ${item.icon} me-2`}></i>
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="nav-section">
            <h6 className="nav-section-title">ACCOUNT</h6>
            <ul className="nav-list">
              <button className="nav-link" onClick={handleLogout}>
                <i className="fas fa-sign-out-alt me-2"></i>
                Logout
              </button>
            </ul>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="main-content">
        <header className="top-header">
          <div className="header-left">
            <button className="sidebar-toggle d-lg-none" onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}>
              <i className="fas fa-bars"></i>
            </button>
            <div className="page-title">
              <button 
                onClick={() => navigate('/admin/companies')} 
                style={{ background: 'none', border: 'none', cursor: 'pointer', marginRight: '10px', color: '#6b7280' }}
              >
                <i className="fas fa-arrow-left"></i>
              </button>
              <h4 className="mb-0" style={{ color: '#1f2937', fontWeight: '600' }}>Company Details</h4>
            </div>
          </div>
          <div className="header-right">
            <button className="btn btn-primary" onClick={() => navigate(`/admin/companies/${companyId}/edit`)} style={{ backgroundColor: '#F89423', border: 'none', padding: '8px 16px', borderRadius: '6px' }}>
              <i className="fas fa-edit me-2"></i>Edit Company
            </button>
          </div>
        </header>

        <div className="content-wrapper" style={{ padding: '30px' }}>
          {/* Company Header Card */}
          <div className="card mb-4" style={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}>
            <div className="card-body p-4">
              <div className="d-flex align-items-center gap-3 mb-3">
                {company.logo ? (
                  <img 
                    src={company.logo.startsWith('http') ? company.logo : `${API_BASE}${company.logo}`} 
                    alt="Company Logo" 
                    style={{ width: '80px', height: '80px', objectFit: 'contain', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                  />
                ) : (
                  <div style={{ width: '80px', height: '80px', backgroundColor: '#f3f4f6', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="fas fa-building fa-2x text-muted"></i>
                  </div>
                )}
                <div>
                  <h3 className="mb-1" style={{ color: '#1f2937', fontWeight: '700' }}>{company.businessName || 'N/A'}</h3>
                  <div className="d-flex gap-2">
                    <span className={`badge ${company.isOnboarded ? 'bg-success' : 'bg-secondary'}`} style={{ padding: '6px 12px', borderRadius: '12px', fontSize: '12px' }}>
                      {company.isOnboarded ? 'Active' : 'Inactive'}
                    </span>
                    <span className={`badge ${company.isSchedule ? 'bg-info' : 'bg-warning text-dark'}`} style={{ padding: '6px 12px', borderRadius: '12px', fontSize: '12px' }}>
                      {company.isSchedule ? 'Scheduled' : 'Not Scheduled'}
                    </span>
                    <span className={`badge ${company.isCompleteRegistration ? 'bg-success' : 'bg-secondary'}`} style={{ padding: '6px 12px', borderRadius: '12px', fontSize: '12px' }}>
                      {company.isCompleteRegistration ? 'Registration Complete' : 'Registration Incomplete'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row">
            {/* Basic Information */}
            <div className="col-md-6 mb-4">
              <div className="card h-100" style={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                <div className="card-header" style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb', padding: '16px 20px' }}>
                  <h5 className="mb-0" style={{ color: '#1f2937', fontWeight: '600' }}>
                    <i className="fas fa-building me-2 text-orange"></i>Business Information
                  </h5>
                </div>
                <div className="card-body p-4">
                  <div className="mb-3">
                    <label className="text-muted small" style={{ fontSize: '12px', fontWeight: '600' }}>Account Type</label>
                    <p className="mb-0" style={{ color: '#374151', fontWeight: '500' }}>
                      {getDropdownName(dropdownOptions.businessCategories, company.accountType)}
                    </p>
                  </div>
                  <div className="mb-3">
                    <label className="text-muted small" style={{ fontSize: '12px', fontWeight: '600' }}>Business Sector</label>
                    <p className="mb-0" style={{ color: '#374151', fontWeight: '500' }}>
                      {getDropdownName(dropdownOptions.businessSectors, company.businessSector)}
                    </p>
                  </div>
                  <div className="mb-3">
                    <label className="text-muted small" style={{ fontSize: '12px', fontWeight: '600' }}>Created Date</label>
                    <p className="mb-0" style={{ color: '#374151', fontWeight: '500' }}>
                      {company.createdDate ? new Date(company.createdDate).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                  <div className="mb-3">
                    <label className="text-muted small" style={{ fontSize: '12px', fontWeight: '600' }}>Subscription Period</label>
                    <p className="mb-0" style={{ color: '#374151', fontWeight: '500' }}>
                      {company.startDate ? new Date(company.startDate).toLocaleDateString() : 'N/A'} - {company.endDate ? new Date(company.endDate).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="col-md-6 mb-4">
              <div className="card h-100" style={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                <div className="card-header" style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb', padding: '16px 20px' }}>
                  <h5 className="mb-0" style={{ color: '#1f2937', fontWeight: '600' }}>
                    <i className="fas fa-user me-2 text-orange"></i>Contact Information
                  </h5>
                </div>
                <div className="card-body p-4">
                  <div className="mb-3">
                    <label className="text-muted small" style={{ fontSize: '12px', fontWeight: '600' }}>Contact Person</label>
                    <p className="mb-0" style={{ color: '#374151', fontWeight: '500' }}>
                      {getDropdownName(dropdownOptions.titles, company.title)} {company.firstName} {company.lastName}
                    </p>
                  </div>
                  <div className="mb-3">
                    <label className="text-muted small" style={{ fontSize: '12px', fontWeight: '600' }}>Email Address</label>
                    <p className="mb-0">
                      <a href={`mailto:${company.email}`} style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: '500' }}>
                        {company.email || 'N/A'}
                      </a>
                    </p>
                  </div>
                  <div className="mb-3">
                    <label className="text-muted small" style={{ fontSize: '12px', fontWeight: '600' }}>Phone Number</label>
                    <p className="mb-0" style={{ color: '#374151', fontWeight: '500' }}>
                      <a href={`tel:${company.phoneNumber}`} style={{ color: '#3b82f6', textDecoration: 'none' }}>
                        {company.phoneNumber || 'N/A'}
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Documents Section */}
          <div className="card mb-4" style={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}>
            <div className="card-header" style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb', padding: '16px 20px' }}>
              <h5 className="mb-0" style={{ color: '#1f2937', fontWeight: '600' }}>
                <i className="fas fa-file-alt me-2 text-orange"></i>Documents
              </h5>
            </div>
            <div className="card-body p-4">
              <div className="row">
                {/* NIN Document */}
                <div className="col-md-4 mb-3">
                  <div className="border rounded p-3 text-center" style={{ borderColor: '#e5e7eb' }}>
                    <i className="fas fa-id-card fa-3x mb-3" style={{ color: company.ninDocument ? '#10b981' : '#9ca3af' }}></i>
                    <h6 className="mb-2" style={{ fontWeight: '600', color: '#374151' }}>NIN Document</h6>
                    {company.ninDocument ? (
                      <>
                        <span className="badge bg-success mb-3" style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '12px' }}>
                          <i className="fas fa-check-circle me-1"></i> Uploaded
                        </span>
                        <div className="d-flex gap-2 justify-content-center">
                          <button 
                            className="btn btn-sm btn-primary" 
                            onClick={() => handlePreviewDocument(company.ninDocument, 'document', 'NIN Document')}
                            style={{ backgroundColor: '#F89423', border: 'none' }}
                          >
                            <i className="fas fa-eye me-1"></i> View
                          </button>
                          <a 
                            href={company.ninDocument.startsWith('http') ? company.ninDocument : `${API_BASE}${company.ninDocument}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-secondary"
                          >
                            <i className="fas fa-download me-1"></i> Download
                          </a>
                        </div>
                      </>
                    ) : (
                      <span className="badge bg-danger" style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '12px' }}>
                        <i className="fas fa-times-circle me-1"></i> Not Uploaded
                      </span>
                    )}
                  </div>
                </div>

                {/* CAC Document */}
                <div className="col-md-4 mb-3">
                  <div className="border rounded p-3 text-center" style={{ borderColor: '#e5e7eb' }}>
                    <i className="fas fa-file-contract fa-3x mb-3" style={{ color: company.cacDocument ? '#10b981' : '#9ca3af' }}></i>
                    <h6 className="mb-2" style={{ fontWeight: '600', color: '#374151' }}>CAC Document</h6>
                    {company.cacDocument ? (
                      <>
                        <span className="badge bg-success mb-3" style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '12px' }}>
                          <i className="fas fa-check-circle me-1"></i> Uploaded
                        </span>
                        <div className="d-flex gap-2 justify-content-center">
                          <button 
                            className="btn btn-sm btn-primary" 
                            onClick={() => handlePreviewDocument(company.cacDocument, 'document', 'CAC Document')}
                            style={{ backgroundColor: '#F89423', border: 'none' }}
                          >
                            <i className="fas fa-eye me-1"></i> View
                          </button>
                          <a 
                            href={company.cacDocument.startsWith('http') ? company.cacDocument : `${API_BASE}${company.cacDocument}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-secondary"
                          >
                            <i className="fas fa-download me-1"></i> Download
                          </a>
                        </div>
                      </>
                    ) : (
                      <span className="badge bg-danger" style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '12px' }}>
                        <i className="fas fa-times-circle me-1"></i> Not Uploaded
                      </span>
                    )}
                  </div>
                </div>

                {/* Company Logo */}
                <div className="col-md-4 mb-3">
                  <div className="border rounded p-3 text-center" style={{ borderColor: '#e5e7eb' }}>
                    <i className="fas fa-image fa-3x mb-3" style={{ color: company.logo ? '#10b981' : '#9ca3af' }}></i>
                    <h6 className="mb-2" style={{ fontWeight: '600', color: '#374151' }}>Company Logo</h6>
                    {company.logo ? (
                      <>
                        <span className="badge bg-success mb-3" style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '12px' }}>
                          <i className="fas fa-check-circle me-1"></i> Uploaded
                        </span>
                        <div className="d-flex gap-2 justify-content-center">
                          <button 
                            className="btn btn-sm btn-primary" 
                            onClick={() => handlePreviewDocument(company.logo, 'image', 'Company Logo')}
                            style={{ backgroundColor: '#F89423', border: 'none' }}
                          >
                            <i className="fas fa-eye me-1"></i> View
                          </button>
                        </div>
                      </>
                    ) : (
                      <span className="badge bg-danger" style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '12px' }}>
                        <i className="fas fa-times-circle me-1"></i> Not Uploaded
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Document Preview Modal */}
      {previewModal.show && (
        <div 
          className="modal-overlay" 
          onClick={handleClosePreview} 
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            width: '100%', 
            height: '100%', 
            background: 'rgba(0,0,0,0.8)', 
            zIndex: 3000, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            padding: '20px'
          }}
        >
          <div 
            className="modal-panel" 
            onClick={(e) => e.stopPropagation()} 
            style={{ 
              background: 'white', 
              borderRadius: '12px', 
              width: '90%', 
              maxWidth: '900px', 
              maxHeight: '90vh', 
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div className="modal-header" style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h5 style={{ margin: 0, color: '#1f2937' }}>{previewModal.title}</h5>
              <button onClick={handleClosePreview} style={{ background: 'none', border: 'none', fontSize: '24px', color: '#6b7280', cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {previewModal.type === 'image' ? (
                <img src={previewModal.url} alt={previewModal.title} style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
              ) : (
                <iframe 
                  src={previewModal.url} 
                  title={previewModal.title}
                  style={{ width: '100%', height: '70vh', border: 'none' }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .admin-page { display: flex; min-height: 100vh; background-color: #f8f9fa; }
        .sidebar {
          width: 260px; background: white; color: #333; position: fixed; left: 0; top: 0;
          height: 100vh; overflow-y: auto; transition: transform 0.3s ease; z-index: 1000;
          border-right: 1px solid #e5e7eb;
        }
        .sidebar-header { padding: 20px; border-bottom: 1px solid #e5e7eb; }
        .brand-logo { color: #333; font-size: 24px; font-weight: 700; text-decoration: none; display: flex; align-items: center; }
        .sidebar-nav { padding: 20px 0; }
        .nav-section { margin-bottom: 25px; }
        .nav-section-title {
          font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px;
          color: #F89423; padding: 0 20px; margin-bottom: 10px;
        }
        .nav-list { list-style: none; padding: 0; margin: 0; }
        .nav-link {
          display: flex; align-items: center; padding: 12px 20px; color: #333; text-decoration: none;
          transition: all 0.3s ease; font-weight: 500;
        }
        .nav-link:hover, .nav-link.active { 
          background-color: #F89423; 
          color: white; 
          border-left: 4px solid white; 
        }
        .sidebar-footer { margin-top: auto; padding-bottom: 20px; }
        .main-content { flex: 1; margin-left: 260px; display: flex; flex-direction: column; }
        .top-header { 
          background: white; padding: 15px 30px; box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          display: flex; justify-content: space-between; align-items: center; position: sticky; top: 0; z-index: 100;
        }
        .header-left { display: flex; align-items: center; gap: 20px; }
        .header-right { display: flex; align-items: center; gap: 15px; }
        .content-wrapper { padding: 30px; flex: 1; }
        .text-orange { color: #F89423; }
        .modal-overlay { animation: fadeIn 0.3s ease; }
        .modal-panel { animation: slideIn 0.3s ease; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideIn { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @media (max-width: 992px) { 
          .sidebar { transform: translateX(-100%); } 
          .sidebar.sidebar-open { transform: translateX(0); } 
          .main-content { margin-left: 0; } 
        }
      `}</style>
    </div>
  );
}

export default ViewCompany;